import { type ReactNode, useCallback, useEffect } from 'react';

import {
  Box,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import {
  ANIMATIONS,
  DIMENSIONS,
} from '@modules/_shared/components/report-editor/constants';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import ReportEditorStepperListItem from '@modules/_shared/components/report-editor/ReportEditorStepperListItem';
import { useLocale } from '@shared/hooks/locale';

export type StepType = {
  title: string;
  icon: ReactNode;
  position: 'main' | 'footer';
  pageId?: string;
  [key: string]: any;
};

export type ReportEditorStepperProps = {
  children: ReactNode;
};

const ReportEditorStepper = ({ children }: ReportEditorStepperProps) => {
  const { breakpoints } = useTheme();
  const isTablet = useMediaQuery(breakpoints.down('lg'));
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const { t } = useLocale();
  const {
    isSidebarExpanded,
    setSidebarExpanded,
    currentStepData,
    mainSteps,
    footerSteps,
  } = useReportContext();

  useEffect(() => {
    if (isTablet) {
      setSidebarExpanded(false);
    }
  }, [isTablet, setSidebarExpanded]);

  const handleExpandToggle = useCallback(() => {
    setSidebarExpanded(!isSidebarExpanded);
  }, [isSidebarExpanded, setSidebarExpanded]);

  // If mobile, we don't display vertical stepper, just the step component
  if (isMobile) {
    return children;
  }

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      <Stack
        sx={{
          width: isSidebarExpanded
            ? DIMENSIONS.SIDEBAR_EXPANDED_WIDTH
            : DIMENSIONS.SIDEBAR_COLLAPSED_WIDTH,
          height: '100%',
          minHeight: 0,
          flexShrink: 0,
          borderRight: '1px solid',
          borderColor: 'divider',
          transition: `width ${ANIMATIONS.TRANSITION_DURATION} ${ANIMATIONS.TRANSITION_TIMING}`,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            height: 57,
            minHeight: 57,
          }}
        >
          {isSidebarExpanded ? (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, ml: 2.2 }}>
                {t('Page')}
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <IconButton onClick={handleExpandToggle} sx={{ mr: 0.5 }}>
                <ChevronLeft size={20} />
              </IconButton>
            </>
          ) : (
            <IconButton onClick={handleExpandToggle} sx={{ mx: 'auto' }}>
              <ChevronRight size={20} />
            </IconButton>
          )}
        </Box>

        {/* Steps list */}
        <Stack
          sx={{
            justifyContent: 'space-between',
            flexGrow: 1,
            minHeight: 0,
          }}
        >
          <Box sx={{ overflow: 'auto', flexGrow: 1, height: 0 }}>
            {mainSteps.map((item, index) => (
              <ReportEditorStepperListItem
                key={item.pageId ?? index}
                item={item}
                index={index}
                isExpanded={isSidebarExpanded}
                isActive={currentStepData.pageId === item.pageId}
              />
            ))}
          </Box>
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            {footerSteps.map((item, index) => (
              <ReportEditorStepperListItem
                key={item.pageId ?? mainSteps.length + index}
                item={item}
                index={mainSteps.length + index}
                isExpanded={isSidebarExpanded}
                isActive={currentStepData.pageId === item.pageId}
              />
            ))}
          </Box>
        </Stack>
      </Stack>
      {children}
    </Box>
  );
};

export default ReportEditorStepper;
